// File: frontend/src/services/adminMatchService.ts
// src/services/adminMatchService.ts

import { apiClient } from "./apiClient";
import {
  AdminMatchDto,
  AdminMatchPageResponse,
  AdminMatchFilters,
  CreateMatchByAdminRequest,
  SaveMatchRefereeRequest,
  UpdateMatchByAdminRequest,
  UpdateMatchScoreRequest,
  UpdateMatchStatusRequest,
} from "../types/adminMatch.types";

const BASE_URL = "/api/admin/matches";

/**
 * Build query string from filters
 */
const buildQueryString = (
  page: number,
  size: number,
  filters: AdminMatchFilters
): string => {
  const params = new URLSearchParams();
  params.append("page", page.toString());
  params.append("size", size.toString());
  params.append("sortBy", filters.sortBy);
  params.append("sortDir", filters.sortDir);

  if (filters.status) {
    params.append("status", filters.status);
  }
  if (filters.sportType) {
    params.append("sportType", filters.sportType);
  }
  if (filters.matchGender) {
    params.append("matchGender", filters.matchGender);
  }
  // Country bucket filter — "INTL" means cross-country matches
  if (filters.country) {
    params.append("country", filters.country);
  }
  if (filters.search) {
    params.append("search", filters.search);
  }
  if (filters.startDate) {
    params.append("startDate", filters.startDate);
  }
  if (filters.endDate) {
    params.append("endDate", filters.endDate);
  }
  // Public visibility filter — only sent when explicitly chosen
  if (filters.active !== undefined && filters.active !== "") {
    params.append("active", filters.active);
  }
  // Lock status filter — only sent when explicitly chosen
  if (filters.locked !== undefined && filters.locked !== "") {
    params.append("locked", filters.locked);
  }

  return params.toString();
};

/**
 * Get paginated list of matches with filters
 */
export const getAdminMatches = async (
  page: number = 0,
  size: number = 20,
  filters: AdminMatchFilters = { sortBy: "matchDate", sortDir: "desc" }
): Promise<AdminMatchPageResponse> => {
  const queryString = buildQueryString(page, size, filters);
  return apiClient.get<AdminMatchPageResponse>(`${BASE_URL}?${queryString}`);
};

/**
 * Get single match by ID
 */
export const getAdminMatchById = async (matchId: number): Promise<AdminMatchDto> => {
  return apiClient.get<AdminMatchDto>(`${BASE_URL}/${matchId}`);
};

/**
 * Create a new match.
 * @param force - if true, bypasses the same-day duplicate warning (SAME_DAY_DUPLICATE).
 *                Exact duplicates are always blocked by the backend regardless.
 */
export const createAdminMatch = async (
  request: CreateMatchByAdminRequest,
  force: boolean = false
): Promise<AdminMatchDto> => {
  const url = force ? `${BASE_URL}?force=true` : BASE_URL;
  return apiClient.post<AdminMatchDto>(url, request);
};

/**
 * Update an existing match
 */
export const updateAdminMatch = async (
  matchId: number,
  request: UpdateMatchByAdminRequest
): Promise<AdminMatchDto> => {
  return apiClient.put<AdminMatchDto>(`${BASE_URL}/${matchId}`, request);
};

/**
 * Update match score
 */
export const updateMatchScore = async (
  matchId: number,
  request: UpdateMatchScoreRequest
): Promise<AdminMatchDto> => {
  return apiClient.put<AdminMatchDto>(`${BASE_URL}/${matchId}/score`, request);
};

/**
 * Update match status
 */
export const updateMatchStatus = async (
  matchId: number,
  request: UpdateMatchStatusRequest
): Promise<AdminMatchDto> => {
  return apiClient.put<AdminMatchDto>(`${BASE_URL}/${matchId}/status`, request);
};

/**
 * Delete a match (soft delete, ADMIN only)
 */
export const deleteAdminMatch = async (matchId: number): Promise<void> => {
  return apiClient.delete<void>(`${BASE_URL}/${matchId}`);
};

/**
 * Add a referee to a match.
 * Returns the updated AdminMatchDto with the new referee in the referees list.
 */
export const addMatchReferee = async (
  matchId: number,
  request: SaveMatchRefereeRequest
): Promise<AdminMatchDto> => {
  return apiClient.post<AdminMatchDto>(`${BASE_URL}/${matchId}/referees`, request);
};

/**
 * Remove a referee assignment from a match.
 * Returns the updated AdminMatchDto with the referee removed from the referees list.
 */
export const removeMatchReferee = async (
  matchId: number,
  assignmentId: number
): Promise<AdminMatchDto> => {
  return apiClient.delete<AdminMatchDto>(`${BASE_URL}/${matchId}/referees/${assignmentId}`);
};

/** Quick-toggle public visibility — convenience wrapper for the table row toggle button */
export const toggleMatchActive = (id: number, active: boolean): Promise<AdminMatchDto> =>
  apiClient.put<AdminMatchDto>(`${BASE_URL}/${id}`, { active });

/** Lock or unlock a match topic — blocks/allows new entry submissions (ADMIN or MODERATOR) */
export const setMatchLocked = (id: number, locked: boolean, lockReason?: string, lockReasonVisible?: boolean): Promise<AdminMatchDto> =>
  apiClient.patch<AdminMatchDto>(`/api/admin/matches/${id}/lock`, { locked, lockReason, lockReasonVisible });